import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { BookmakerCard } from "../components/BookmakerCard";
import { CountrySelector } from "../components/CountrySelector";
import { SEO } from "../components/SEO";
import { EmptyState, SectionHeading } from "../components/ui";
import { useCountry } from "../context/CountryContext";
import { bookmakers, countries } from "../data/demo";

export function BettingSitesPage() {
  const params = useParams();
  const { country, setCountryCode } = useCountry();
  const routeCountry = countries.find((item) => item.slug === params.country);
  useEffect(() => {
    if (routeCountry && routeCountry.code !== country.code)
      setCountryCode(routeCountry.code);
  }, [routeCountry, country.code, setCountryCode]);
  const effective = routeCountry || country;
  const filtered = useMemo(
    () =>
      effective.code === "global"
        ? bookmakers
        : bookmakers.filter((bookmaker) =>
            bookmaker.countries.includes(effective.code),
          ),
    [effective.code],
  );
  const title =
    effective.code === "global"
      ? "Compare betting sites"
      : `Best betting sites in ${effective.name}`;
  return (
    <div className="page-shell py-10 sm:py-14">
      <SEO
        title={title}
        description={`Compare verified bookmaker features, payments and editorial scores for ${effective.name}.`}
        path={
          params.country ? `/${params.country}/betting-sites` : "/betting-sites"
        }
      />
      <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-start">
        <SectionHeading
          headingLevel={1}
          eyebrow="Independent comparison"
          title={title}
          description="Availability, payments and promotions vary by country. Rankings use editorial product criteria—not affiliate commission."
        />
        <CountrySelector />
      </div>
      {filtered.length ? (
        <div className="grid gap-4 lg:grid-cols-3">
          {filtered
            .sort((a, b) => b.rating - a.rating)
            .map((bookmaker, index) => (
              <BookmakerCard
                bookmaker={bookmaker}
                rank={index + 1}
                key={bookmaker.id}
              />
            ))}
        </div>
      ) : (
        <EmptyState
          title="No verified sites yet"
          description="We do not show a bookmaker until its availability has been checked for the selected country."
        />
      )}
      <section className="surface mt-10 rounded-2xl p-6">
        <h2 className="display text-xl font-black">
          How we rank betting sites
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-muted">
          The overall score blends odds quality, sports coverage, mobile
          experience, withdrawals and support. Sponsored placements are labelled
          and do not overwrite the editorial score. Details are reviewed
          periodically, but always confirm current terms with the operator.
        </p>
      </section>
    </div>
  );
}
